import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import "./SettingsPage.responsive.css";

const FALLBACK_FREE_PATIENT_LIMIT = 2;
const FALLBACK_FREE_SESSIONS_PER_PATIENT = 2;
const FALLBACK_TRIAL_DAYS = 7;

function safeDate(value) {
  if (!value) return null;

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDate(value) {
  const date = safeDate(value);
  if (!date) return "—";

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getDaysLeft(endDate) {
  const date = safeDate(endDate);
  if (!date) return 0;

  const diff = date.getTime() - new Date().getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function usagePercent(value, limit) {
  const safeLimit = Math.max(Number(limit || 0), 1);
  return Math.min(100, Math.round((Number(value || 0) / safeLimit) * 100));
}

function SettingsStatCard({
  label,
  value,
  helper,
  primary = false,
  locked = false,
}) {
  return (
    <div className={primary ? "mgsettings-stat primary" : "mgsettings-stat"}>
      <div className="mgsettings-stat-top">
        <span>{label}</span>
        {locked && <em>Admin controlled</em>}
      </div>

      <strong>{value}</strong>
      <p>{helper}</p>
    </div>
  );
}

function UsageBar({ value, limit, tone = "blue" }) {
  return (
    <div className={`mgsettings-usage-bar ${tone}`}>
      <i style={{ width: `${usagePercent(value, limit)}%` }} />
    </div>
  );
}

function PlanSignal({
  totalPatients,
  totalSessions,
  daysLeft,
  patientLimit,
  sessionsPerPatient,
  trialDays,
}) {
  const totalSessionLimit = patientLimit * sessionsPerPatient;

  const maxValue = Math.max(
    patientLimit,
    totalSessionLimit,
    trialDays,
    totalPatients,
    totalSessions,
    daysLeft,
    1
  );

  const bars = [
    {
      label: "Patients",
      value: totalPatients,
      limit: patientLimit,
      color: "linear-gradient(180deg, #18b7ff, #1f7bc1)",
    },
    {
      label: "Sessions",
      value: totalSessions,
      limit: totalSessionLimit,
      color: "linear-gradient(180deg, #62d7ff, #2d8fe0)",
    },
    {
      label: "Validity",
      value: daysLeft,
      limit: trialDays,
      color: "linear-gradient(180deg, #fbbf24, #f59e0b)",
    },
  ];

  return (
    <div className="mgsettings-signal-card">
      <div className="mgsettings-signal-head">
        <div>
          <span>Usage Signal</span>
          <strong>Workspace quota activity</strong>
        </div>
        <em>Live</em>
      </div>

      <div className="mgsettings-bars">
        {bars.map((bar) => {
          const height = Math.max(22, Math.round((bar.value / maxValue) * 100));

          return (
            <div className="mgsettings-bar-item" key={bar.label}>
              <div className="mgsettings-bar-track">
                <i
                  style={{
                    height: `${height}%`,
                    background: bar.color,
                  }}
                />
              </div>

              <b>
                {bar.value}/{bar.limit}
              </b>

              <small>{bar.label}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlanWorkflow({
  totalPatients,
  totalSessions,
  daysLeft,
  patientLimit,
  sessionsPerPatient,
  trialDays,
  patientLimitReached,
  sessionLimitReached,
  trialExpired,
  quotaEnabled,
}) {
  let nextAction = "Continue your clinical workspace.";

  if (!quotaEnabled) {
    nextAction = "Workspace quota is disabled. Contact admin to restore access.";
  } else if (trialExpired) {
    nextAction = "Workspace validity has expired. Contact us to continue access.";
  } else if (patientLimitReached || sessionLimitReached) {
    nextAction = "Usage limit reached. Contact us to increase quota.";
  } else if (daysLeft <= 2) {
    nextAction = "Workspace validity is near expiry. Contact us to extend access.";
  }

  const totalSessionLimit = patientLimit * sessionsPerPatient;

  const steps = [
    {
      title: "Access",
      helper: quotaEnabled ? `${trialDays} days` : "Disabled",
      active: quotaEnabled,
    },
    {
      title: "Patients",
      helper: `${totalPatients}/${patientLimit}`,
      active: totalPatients > 0,
    },
    {
      title: "Sessions",
      helper: `${totalSessions}/${totalSessionLimit}`,
      active: totalSessions > 0,
    },
    {
      title: "Admin Quota",
      helper: "Controlled",
      active: true,
    },
    {
      title: "Upgrade",
      helper: "Contact us",
      active:
        !quotaEnabled ||
        patientLimitReached ||
        sessionLimitReached ||
        trialExpired ||
        daysLeft <= 2,
    },
  ];

  return (
    <div className="mgsettings-workflow-card">
      <div className="mgsettings-workflow-head">
        <div>
          <span>Plan Intelligence</span>
          <strong>Workspace usage pipeline</strong>
        </div>
        <em>{daysLeft} days left</em>
      </div>

      <div className="mgsettings-flow">
        {steps.map((step, index) => (
          <div
            key={step.title}
            className={
              step.active
                ? "mgsettings-flow-step active"
                : "mgsettings-flow-step"
            }
          >
            <b>{index + 1}</b>
            <span>{step.title}</span>
            <small>{step.helper}</small>
          </div>
        ))}
      </div>

      <div className="mgsettings-next-action">
        <span>Next Action</span>
        <strong>{nextAction}</strong>
        <p>
          Patient and session limits are read from your secure account quota.
          Users cannot edit quota here. Admin can change quota, expiry date, and
          access state from the admin panel.
        </p>
      </div>
    </div>
  );
}

export default function SettingsPage({
  user,
  theme,
  onToggleTheme,
  onLogout,
}) {
  const [patients, setPatients] = useState([]);
  const [sessionMap, setSessionMap] = useState({});
  const [quota, setQuota] = useState(null);

  const [loadingUsage, setLoadingUsage] = useState(true);
  const [loadingQuota, setLoadingQuota] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    await Promise.all([loadUsage(), loadMyQuota()]);
  };

  const clearLocalSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_id");
    localStorage.removeItem("ui_theme");
    window.location.reload();
  };

  const loadMyQuota = async () => {
    try {
      setLoadingQuota(true);

      /*
        No user id header here.
        Axios interceptor sends:
        Authorization: Bearer <token>

        Backend must decode token and return quota for current logged-in user.
      */
      const res = await api.get("/api/admin/usage/my-quota");

      setQuota(res.data || null);
    } catch (e) {
      console.error("Could not load quota", e);
      setQuota(null);
    } finally {
      setLoadingQuota(false);
    }
  };

  const loadUsage = async () => {
    try {
      setLoadingUsage(true);

      const patientRes = await api.get("/api/patient/");
      const patientList = patientRes.data || [];
      setPatients(patientList);

      const sessionResults = await Promise.all(
        patientList.map(async (patient) => {
          try {
            const sessionRes = await api.get(`/api/session/patient/${patient.id}`);
            return [patient.id, sessionRes.data || []];
          } catch {
            return [patient.id, []];
          }
        })
      );

      const nextMap = {};
      sessionResults.forEach(([patientId, sessions]) => {
        nextMap[patientId] = sessions;
      });

      setSessionMap(nextMap);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load usage settings");
    } finally {
      setLoadingUsage(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadUsage(), loadMyQuota()]);
  };

  const patientLimit = Number(
    quota?.patient_limit ?? FALLBACK_FREE_PATIENT_LIMIT
  );

  const sessionsPerPatient = Number(
    quota?.sessions_per_patient ?? FALLBACK_FREE_SESSIONS_PER_PATIENT
  );

  const totalAllowedSessions = Number(
    quota?.total_session_limit ?? patientLimit * sessionsPerPatient
  );

  const quotaEnabled = quota?.is_enabled ?? true;

  const quotaNote = quota?.note || "";
  const quotaUpdatedAt = quota?.updated_at || null;

  const createdAt =
    user?.created_at ||
    user?.createdAt ||
    localStorage.getItem("user_created_at") ||
    new Date().toISOString();

  const trialStart = safeDate(createdAt) || new Date();

  const trialEnd = quota?.valid_until
    ? safeDate(quota.valid_until)
    : addDays(trialStart, FALLBACK_TRIAL_DAYS);

  const daysLeft = getDaysLeft(trialEnd);

  const totalSessions = useMemo(() => {
    return Object.values(sessionMap).reduce(
      (sum, sessions) => sum + (sessions?.length || 0),
      0
    );
  }, [sessionMap]);

  const patientUsageRows = useMemo(() => {
    return patients.map((patient) => {
      const sessions = sessionMap[patient.id] || [];
      const used = sessions.length;
      const remaining = Math.max(sessionsPerPatient - used, 0);

      return {
        patient,
        used,
        remaining,
        locked: used >= sessionsPerPatient,
      };
    });
  }, [patients, sessionMap, sessionsPerPatient]);

  const patientLimitReached = patients.length >= patientLimit;
  const sessionLimitReached = totalSessions >= totalAllowedSessions;
  const trialExpired = daysLeft <= 0;

  const accountState = !quotaEnabled
    ? "Quota disabled"
    : trialExpired
    ? "Validity expired"
    : patientLimitReached || sessionLimitReached
    ? "Usage limit reached"
    : "Workspace active";

  const canUseMore =
    quotaEnabled && !trialExpired && !patientLimitReached && !sessionLimitReached;

  return (
    <div className="mgsettings-page">
      <style>{settingsCss}</style>

      <section className="mgsettings-hero">
        <div className="mgsettings-hero-copy">
          <div className="mgsettings-kicker">Settings</div>

          <h1>Workspace Settings</h1>

          <p>
            Manage profile, appearance, workspace quota, patient usage, session
            usage, and local account behavior from one MedGen workspace.
          </p>

          <div className="mgsettings-actions">
            <button
              className="mgsettings-btn primary"
              type="button"
              onClick={refreshAll}
              disabled={loadingUsage || loadingQuota}
            >
              {loadingUsage || loadingQuota ? "Refreshing..." : "Refresh Usage"}
            </button>

            <button
              className="mgsettings-btn soft"
              type="button"
              onClick={onToggleTheme}
            >
              Current theme: {theme === "light" ? "Light" : "Dark"}
            </button>

            <button
              className="mgsettings-btn ghost"
              type="button"
              onClick={onLogout}
            >
              Logout
            </button>
          </div>

          <PlanWorkflow
            totalPatients={patients.length}
            totalSessions={totalSessions}
            daysLeft={daysLeft}
            patientLimit={patientLimit}
            sessionsPerPatient={sessionsPerPatient}
            trialDays={FALLBACK_TRIAL_DAYS}
            patientLimitReached={patientLimitReached}
            sessionLimitReached={sessionLimitReached}
            trialExpired={trialExpired}
            quotaEnabled={quotaEnabled}
          />
        </div>

        <div className="mgsettings-hero-panel">
          <div className="mgsettings-panel-top">
            <div>
              <span>Account Status</span>
              <strong>{accountState}</strong>
            </div>
            <em>{canUseMore ? "Active" : "Contact"}</em>
          </div>

          <p>
            Your account currently allows {patientLimit} patients and{" "}
            {sessionsPerPatient} sessions per patient. These limits are
            controlled by the platform admin.
          </p>

          <PlanSignal
            totalPatients={patients.length}
            totalSessions={totalSessions}
            daysLeft={daysLeft}
            patientLimit={patientLimit}
            sessionsPerPatient={sessionsPerPatient}
            trialDays={FALLBACK_TRIAL_DAYS}
          />
        </div>
      </section>

      <section className="mgsettings-stats-grid">
        <SettingsStatCard
          primary
          locked
          label="Patient quota"
          value={
            loadingQuota
              ? "Loading..."
              : `${patients.length}/${patientLimit}`
          }
          helper="Maximum patient profiles allowed for this account"
        />

        <SettingsStatCard
          locked
          label="Session quota"
          value={
            loadingQuota
              ? "Loading..."
              : `${totalSessions}/${totalAllowedSessions}`
          }
          helper={`${sessionsPerPatient} sessions allowed per patient`}
        />

        <SettingsStatCard
          locked
          label="Validity"
          value={loadingQuota ? "Loading..." : `${daysLeft} days`}
          helper={`Access valid until ${formatDate(trialEnd)}`}
        />

        <SettingsStatCard
          locked
          label="Access mode"
          value={canUseMore ? "Usable" : "Contact us"}
          helper="Quota is managed from the admin panel"
        />
      </section>

      <section className="mgsettings-grid">
        <div className="mgsettings-card">
          <div className="mgsettings-card-head">
            <div>
              <span>Profile</span>
              <strong>User workspace</strong>
              <p>
                Basic account identity used inside this clinical workspace.
              </p>
            </div>
          </div>

          <div className="mgsettings-summary-grid">
            <div className="mgsettings-summary-item">
              <span>Name</span>
              <strong>{user?.name || "User"}</strong>
            </div>

            <div className="mgsettings-summary-item">
              <span>Role</span>
              <strong>{user?.role || "user"}</strong>
            </div>

            <div className="mgsettings-summary-item">
              <span>Workspace</span>
              <strong>MedGen Platform</strong>
            </div>

            <div className="mgsettings-summary-item">
              <span>Quota source</span>
              <strong>{quota ? "Admin configured" : "Default fallback"}</strong>
            </div>
          </div>
        </div>

        <div className="mgsettings-card">
          <div className="mgsettings-card-head">
            <div>
              <span>Appearance</span>
              <strong>Interface mode</strong>
              <p>
                Switch between light clinical mode and dark dense-review mode.
              </p>
            </div>
          </div>

          <div className="mgsettings-mode-card">
            <div>
              <span>Current Theme</span>
              <strong>
                {theme === "light" ? "Light Clinical Mode" : "Dark AI Mode"}
              </strong>
              <p>Theme is saved locally for your browser session.</p>
            </div>

            <button
              className="mgsettings-btn primary"
              type="button"
              onClick={onToggleTheme}
            >
              Toggle Theme
            </button>
          </div>
        </div>

        <div className="mgsettings-card wide">
          <div className="mgsettings-card-head">
            <div>
              <span>Usage Rules</span>
              <strong>Account quota</strong>
              <p>
                These values are non-editable from user settings. Admin panel
                controls patient limit, sessions per patient, validity, and
                account access state.
              </p>
            </div>

            <em>{loadingQuota ? "Loading" : quotaEnabled ? "Enabled" : "Disabled"}</em>
          </div>

          <div className="mgsettings-rules-grid">
            <div className="mgsettings-rule">
              <span>Patient limit</span>
              <strong>{loadingQuota ? "..." : patientLimit}</strong>
              <p>Allowed patient profiles for this account</p>
              <UsageBar value={patients.length} limit={patientLimit} />
            </div>

            <div className="mgsettings-rule">
              <span>Sessions per patient</span>
              <strong>{loadingQuota ? "..." : sessionsPerPatient}</strong>
              <p>Allowed sessions for each patient profile</p>
              <UsageBar
                value={Math.min(
                  sessionsPerPatient,
                  patientUsageRows.reduce(
                    (max, row) => Math.max(max, row.used),
                    0
                  )
                )}
                limit={sessionsPerPatient}
                tone="cyan"
              />
            </div>

            <div className="mgsettings-rule">
              <span>Total session limit</span>
              <strong>{loadingQuota ? "..." : totalAllowedSessions}</strong>
              <p>Total session allowance across patient quota</p>
              <UsageBar
                value={totalSessions}
                limit={totalAllowedSessions}
                tone="yellow"
              />
            </div>
          </div>

          <div className="mgsettings-quota-note">
            <div>
              <span>Valid until</span>
              <strong>{formatDate(trialEnd)}</strong>
            </div>

            <div>
              <span>Quota updated</span>
              <strong>{formatDate(quotaUpdatedAt)}</strong>
            </div>

            <div>
              <span>Admin note</span>
              <strong>{quotaNote || "—"}</strong>
            </div>
          </div>
        </div>

        <div className="mgsettings-card wide">
          <div className="mgsettings-card-head">
            <div>
              <span>Patient Usage</span>
              <strong>Per-patient session quota</strong>
              <p>
                Each patient can use {sessionsPerPatient} sessions under your
                current quota. After the limit, session creation should be
                blocked or routed to upgrade/contact flow.
              </p>
            </div>
            <em>
              {loadingUsage ? "Loading" : `${patientUsageRows.length} patients`}
            </em>
          </div>

          {loadingUsage ? (
            <div className="mgsettings-empty">Loading quota usage...</div>
          ) : patientUsageRows.length === 0 ? (
            <div className="mgsettings-empty">
              <h3>No patient profile found</h3>
              <p>Create a patient profile to start using clinical sessions.</p>
            </div>
          ) : (
            <div className="mgsettings-patient-usage-list">
              {patientUsageRows.map((row) => (
                <div
                  key={row.patient.id}
                  className={
                    row.locked
                      ? "mgsettings-patient-row locked"
                      : "mgsettings-patient-row"
                  }
                >
                  <div className="mgsettings-patient-main">
                    <div className="mgsettings-avatar">
                      {row.patient.name?.slice(0, 2).toUpperCase() || "PT"}
                    </div>

                    <div>
                      <strong>{row.patient.name || "Patient"}</strong>
                      <p>
                        {row.patient.age || "—"} yrs ·{" "}
                        {row.patient.gender || "—"} ·{" "}
                        {row.patient.relation || "self"}
                      </p>
                    </div>
                  </div>

                  <div className="mgsettings-patient-quota">
                    <div>
                      <span>Sessions</span>
                      <strong>
                        {row.used}/{sessionsPerPatient}
                      </strong>
                    </div>

                    <UsageBar
                      value={row.used}
                      limit={sessionsPerPatient}
                      tone={row.locked ? "yellow" : "blue"}
                    />

                    <em>
                      {row.locked ? "Limit reached" : `${row.remaining} left`}
                    </em>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mgsettings-card">
          <div className="mgsettings-card-head">
            <div>
              <span>Clinical Preferences</span>
              <strong>Workspace behavior</strong>
              <p>Current operational defaults for the MedGen workflow.</p>
            </div>
          </div>

          <div className="mgsettings-summary-grid">
            <div className="mgsettings-summary-item">
              <span>Default mode</span>
              <strong>Text session</strong>
            </div>

            <div className="mgsettings-summary-item">
              <span>Privacy</span>
              <strong>Restricted by design</strong>
            </div>

            <div className="mgsettings-summary-item">
              <span>Brain map</span>
              <strong>Anatomy enabled</strong>
            </div>

            <div className="mgsettings-summary-item">
              <span>Clinical graph</span>
              <strong>Analysis map</strong>
            </div>
          </div>
        </div>

        <div className="mgsettings-card contact">
          <div className="mgsettings-card-head">
            <div>
              <span>Upgrade</span>
              <strong>Need more usage?</strong>
              <p>
                To add more patients, sessions, longer access, doctor seats, or
                organization-level controls, contact MedGen support/admin.
              </p>
            </div>
          </div>

          <div className="mgsettings-contact-box">
            <strong>Buy / Extend Access</strong>
            <p>
              Current workspace quota is controlled by admin. Paid quota and
              expiry can be assigned from the admin panel.
            </p>

            <div className="mgsettings-contact-actions">
              <a
                className="mgsettings-btn primary"
                href={`https://wa.me/919655504405?text=${encodeURIComponent(
                  "Hi MedGen team, I am using MedGen Platform. I want to increase my patient/session limits. Please share upgrade details."
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact us on WhatsApp
              </a>

              <button
                className="mgsettings-btn soft"
                type="button"
                onClick={refreshAll}
              >
                Recheck quota
              </button>
            </div>
          </div>
        </div>

        <div className="mgsettings-card danger wide">
          <div className="mgsettings-card-head">
            <div>
              <span>System Actions</span>
              <strong>Session controls</strong>
              <p>Logout or clear local browser session data from this device.</p>
            </div>
          </div>

          <div className="mgsettings-system-actions">
            <button
              className="mgsettings-btn soft"
              type="button"
              onClick={onLogout}
            >
              Logout
            </button>

            <button
              className="mgsettings-btn danger"
              type="button"
              onClick={clearLocalSession}
            >
              Clear local session
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

const settingsCss = `
.mgsettings-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;

  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
}

.mgsettings-page * {
  box-sizing: border-box;
}

.mgsettings-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 26px;
  align-items: stretch;
  margin-bottom: 22px;
  padding: 30px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid var(--mg-border);
  box-shadow: 0 20px 55px rgba(7, 17, 31, 0.07);
}

.mgsettings-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgsettings-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(38px, 4vw, 58px);
  line-height: 1.02;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgsettings-hero p {
  max-width: 790px;
  margin: 18px 0 18px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgsettings-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 22px;
}

.mgsettings-btn {
  min-height: 40px;
  padding: 10px 16px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
  text-decoration: none;
}

.mgsettings-btn:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}

.mgsettings-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.22);
}

.mgsettings-btn.soft,
.mgsettings-btn.ghost {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.mgsettings-btn.danger {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.22);
}

.mgsettings-hero-panel {
  padding: 22px;
  border-radius: 22px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.20), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 20px 55px rgba(3, 6, 12, 0.16);
}

.mgsettings-panel-top,
.mgsettings-signal-head,
.mgsettings-workflow-head,
.mgsettings-card-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgsettings-panel-top span,
.mgsettings-signal-head span,
.mgsettings-workflow-head span,
.mgsettings-card-head span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgsettings-panel-top strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgsettings-panel-top em,
.mgsettings-signal-head em,
.mgsettings-workflow-head em,
.mgsettings-card-head em {
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgsettings-hero-panel p {
  color: #a9bdd3;
  margin: 18px 0;
}

.mgsettings-signal-card {
  padding: 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgsettings-signal-head strong {
  color: white;
  font-size: 16px;
}

.mgsettings-bars {
  height: 145px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  align-items: end;
  margin-top: 16px;
}

.mgsettings-bar-item {
  display: grid;
  justify-items: center;
  gap: 7px;
}

.mgsettings-bar-track {
  width: 58px;
  height: 88px;
  display: flex;
  align-items: end;
  padding: 5px;
  border-radius: 16px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.10);
}

.mgsettings-bar-track i {
  width: 100%;
  min-height: 18px;
  border-radius: 12px;
  animation: mgsettingsBarRise 0.75s ease both;
}

.mgsettings-bar-item b {
  color: white;
  font-size: 16px;
  font-weight: 950;
}

.mgsettings-bar-item small {
  color: #a9bdd3;
  font-size: 11px;
  font-weight: 850;
}

.mgsettings-workflow-card {
  margin-top: 10px;
  padding: 20px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 96% 0%, rgba(24, 183, 255, 0.13), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border: 1px solid var(--mg-border);
  box-shadow: 0 14px 38px rgba(7, 17, 31, 0.055);
}

.mgsettings-workflow-head span {
  color: #125a94;
}

.mgsettings-workflow-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 22px;
  font-weight: 950;
  letter-spacing: -0.045em;
}

.mgsettings-workflow-head em {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border-color: rgba(31, 123, 193, 0.18);
}

.mgsettings-flow {
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0 20px;
}

.mgsettings-flow::before {
  content: "";
  position: absolute;
  left: 8%;
  right: 8%;
  top: 21px;
  height: 3px;
  border-radius: 999px;
  background:
    linear-gradient(
      90deg,
      rgba(31,123,193,0.12),
      rgba(24,183,255,0.42),
      rgba(31,123,193,0.12)
    );
  background-size: 220% 100%;
  animation: mgsettingsFlow 2.8s linear infinite;
}

.mgsettings-flow::after {
  content: "";
  position: absolute;
  top: 17px;
  left: 8%;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #18b7ff;
  box-shadow:
    0 0 0 6px rgba(24,183,255,0.12),
    0 0 22px rgba(24,183,255,0.55);
  animation: mgsettingsFlowDot 4.5s ease-in-out infinite;
}

.mgsettings-flow-step {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 5px;
}

.mgsettings-flow-step b {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 15px;
  color: #0b4e84;
  background: #eef6ff;
  border: 1px solid var(--mg-border);
  font-size: 13px;
  font-weight: 950;
}

.mgsettings-flow-step.active b {
  color: white;
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.28), transparent 28%),
    linear-gradient(135deg, #1f7bc1, #0f5f9d);
  border-color: transparent;
  animation: mgsettingsNodeBreath 2.2s ease-in-out infinite;
}

.mgsettings-flow-step span {
  color: var(--mg-text);
  font-size: 12px;
  font-weight: 950;
}

.mgsettings-flow-step small {
  color: var(--mg-muted);
  font-size: 10px;
  font-weight: 750;
}

.mgsettings-next-action {
  padding: 18px;
  border-radius: 18px;
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 34%),
    #ffffff;
  border: 1px solid #d9e8f6;
}

.mgsettings-next-action span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgsettings-next-action strong {
  display: block;
  color: var(--mg-text);
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.035em;
}

.mgsettings-next-action p {
  margin: 10px 0 0;
  color: var(--mg-muted);
  font-size: 13px;
  line-height: 1.55;
}

.mgsettings-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.mgsettings-stat {
  min-height: 128px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.94);
  box-shadow: 0 12px 35px rgba(7, 17, 31, 0.055);
}

.mgsettings-stat.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #125a94);
  border-color: transparent;
}

.mgsettings-stat-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
}

.mgsettings-stat span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgsettings-stat.primary span {
  color: rgba(255,255,255,0.75);
}

.mgsettings-stat em {
  padding: 4px 9px;
  border-radius: 999px;
  font-style: normal;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-size: 10px;
  font-weight: 900;
}

.mgsettings-stat.primary em {
  color: white;
  background: rgba(255,255,255,0.14);
  border-color: rgba(255,255,255,0.18);
}

.mgsettings-stat strong {
  display: block;
  color: var(--mg-text);
  font-size: 28px;
  font-weight: 950;
}

.mgsettings-stat.primary strong {
  color: white;
}

.mgsettings-stat p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgsettings-stat.primary p {
  color: rgba(255,255,255,0.86);
}

.mgsettings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.mgsettings-card {
  padding: 22px;
  border-radius: 22px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  box-shadow: 0 14px 38px rgba(7,17,31,0.06);
}

.mgsettings-card.wide {
  grid-column: 1 / -1;
}

.mgsettings-card.contact {
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 34%),
    #ffffff;
}

.mgsettings-card.danger {
  background:
    radial-gradient(circle at 100% 0%, rgba(239, 68, 68, 0.06), transparent 34%),
    #ffffff;
}

.mgsettings-card-head {
  margin-bottom: 18px;
}

.mgsettings-card-head span {
  color: #125a94;
}

.mgsettings-card-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 24px;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgsettings-card-head p {
  margin: 7px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgsettings-card-head em {
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border-color: rgba(31, 123, 193, 0.18);
}

.mgsettings-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.mgsettings-summary-item {
  min-height: 92px;
  padding: 18px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.mgsettings-summary-item span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgsettings-summary-item strong {
  color: var(--mg-text);
  font-size: 17px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.mgsettings-mode-card,
.mgsettings-contact-box {
  padding: 18px;
  border-radius: 18px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
  display: grid;
  gap: 14px;
}

.mgsettings-mode-card span,
.mgsettings-contact-box span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgsettings-mode-card strong,
.mgsettings-contact-box strong {
  display: block;
  color: var(--mg-text);
  font-size: 18px;
  font-weight: 950;
}

.mgsettings-mode-card p,
.mgsettings-contact-box p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgsettings-rules-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.mgsettings-rule {
  padding: 18px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.mgsettings-rule span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgsettings-rule strong {
  display: block;
  color: var(--mg-text);
  font-size: 28px;
  font-weight: 950;
}

.mgsettings-rule p {
  margin: 8px 0 14px;
  color: var(--mg-muted);
}

.mgsettings-quota-note {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;
}

.mgsettings-quota-note > div {
  min-height: 82px;
  padding: 16px;
  border-radius: 16px;
  background: #eef6ff;
  border: 1px solid #d9e8f6;
}

.mgsettings-quota-note span {
  display: block;
  margin-bottom: 8px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgsettings-quota-note strong {
  color: var(--mg-text);
  font-size: 14px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.mgsettings-usage-bar {
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #dfeaf6;
  border: 1px solid #d1e2f3;
}

.mgsettings-usage-bar i {
  display: block;
  height: 100%;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #18b7ff, #1f7bc1);
  transition: width 0.35s ease;
}

.mgsettings-usage-bar.cyan i {
  background: linear-gradient(90deg, #62d7ff, #2d8fe0);
}

.mgsettings-usage-bar.yellow i {
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
}

.mgsettings-patient-usage-list {
  display: grid;
  gap: 12px;
}

.mgsettings-patient-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 290px;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid var(--mg-border);
  transition: 0.16s ease;
}

.mgsettings-patient-row.locked {
  background:
    radial-gradient(circle at 100% 0%, rgba(245, 158, 11, 0.10), transparent 34%),
    #ffffff;
}

.mgsettings-patient-main {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
}

.mgsettings-avatar {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  color: #0b4e84;
  font-size: 13px;
  font-weight: 950;
  background: #e6f3ff;
  border: 1px solid #d5e8f7;
  flex: 0 0 auto;
}

.mgsettings-patient-main strong {
  display: block;
  color: var(--mg-text);
  font-size: 16px;
  font-weight: 950;
}

.mgsettings-patient-main p {
  margin: 5px 0 0;
  color: var(--mg-muted);
}

.mgsettings-patient-quota {
  display: grid;
  gap: 8px;
}

.mgsettings-patient-quota > div {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.mgsettings-patient-quota span {
  color: #75869d;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgsettings-patient-quota strong {
  color: var(--mg-text);
  font-size: 15px;
  font-weight: 950;
}

.mgsettings-patient-quota em {
  justify-self: start;
  padding: 5px 9px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31, 123, 193, 0.10);
  border: 1px solid rgba(31, 123, 193, 0.18);
  font-style: normal;
  font-size: 11px;
  font-weight: 900;
}

.mgsettings-patient-row.locked .mgsettings-patient-quota em {
  color: #92400e;
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.24);
}

.mgsettings-empty {
  padding: 24px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  color: var(--mg-text);
}

.mgsettings-empty h3 {
  margin: 0 0 8px;
  color: var(--mg-text);
}

.mgsettings-empty p {
  margin: 0;
  color: var(--mg-muted);
}

.mgsettings-contact-actions,
.mgsettings-system-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

@keyframes mgsettingsBarRise {
  from {
    transform: scaleY(0.4);
    opacity: 0.45;
    transform-origin: bottom;
  }

  to {
    transform: scaleY(1);
    opacity: 1;
    transform-origin: bottom;
  }
}

@keyframes mgsettingsFlow {
  0% {
    background-position: 220% 0;
  }

  100% {
    background-position: -220% 0;
  }
}

@keyframes mgsettingsFlowDot {
  0% {
    left: 8%;
    opacity: 0;
    transform: scale(0.82);
  }

  8% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    left: 50%;
  }

  92% {
    opacity: 1;
  }

  100% {
    left: 88%;
    opacity: 0;
    transform: scale(0.82);
  }
}

@keyframes mgsettingsNodeBreath {
  0% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }

  50% {
    box-shadow:
      0 18px 36px rgba(31, 123, 193, 0.28),
      0 0 0 7px rgba(24, 183, 255, 0.06);
  }

  100% {
    box-shadow:
      0 14px 28px rgba(31, 123, 193, 0.22),
      0 0 0 0 rgba(24, 183, 255, 0.18);
  }
}

@media (max-width: 1280px) {
  .mgsettings-hero {
    grid-template-columns: 1fr;
  }

  .mgsettings-hero-panel {
    max-width: 620px;
  }

  .mgsettings-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .mgsettings-page {
    padding: 20px;
  }

  .mgsettings-grid {
    grid-template-columns: 1fr;
  }

  .mgsettings-rules-grid,
  .mgsettings-quota-note {
    grid-template-columns: 1fr;
  }

  .mgsettings-flow {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mgsettings-flow::before,
  .mgsettings-flow::after {
    display: none;
  }

  .mgsettings-flow-step {
    grid-template-columns: 44px 1fr;
    justify-items: start;
    text-align: left;
    padding: 12px;
    border-radius: 16px;
    background: rgba(255,255,255,0.75);
    border: 1px solid #d9e8f6;
  }
}

@media (max-width: 760px) {
  .mgsettings-page {
    padding: 14px;
  }

  .mgsettings-hero {
    padding: 20px;
  }

  .mgsettings-hero h1 {
    font-size: 36px;
  }

  .mgsettings-actions,
  .mgsettings-contact-actions,
  .mgsettings-system-actions {
    flex-direction: column;
  }

  .mgsettings-btn {
    width: 100%;
  }

  .mgsettings-stats-grid {
    grid-template-columns: 1fr;
  }

  .mgsettings-summary-grid {
    grid-template-columns: 1fr;
  }

  .mgsettings-patient-row {
    grid-template-columns: 1fr;
  }

  .mgsettings-bars {
    gap: 8px;
  }

  .mgsettings-bar-track {
    width: 48px;
  }
}
`;